import React, { FC, useMemo } from 'react';

import { StyleSheet } from 'react-native';

import { IBackendCart, ILegacyCartEntry } from '@rbi-ctg/menu';
import Currency from 'components/currency';
import Picture from 'components/picture';
import { Box, BoxProps, Typography } from 'components/ui';
import { DeliveryStatus } from 'generated/graphql-gateway';
import { useCart } from 'hooks/use-cart';
import { useFeatureDisclaimers } from 'hooks/use-feature-disclaimers';
import useIsSignUpAfterCart from 'hooks/use-is-sign-up-after-cart';
import { t } from 'state/intl/translations';
import { layout, primitive, spacing } from 'styles/constants';

import { useCheckoutLoadingState } from '../../hooks';
import { useCheckoutContext } from '../../hooks/use-checkout-context';

import DonationsRow from './donations-row';
import { FeeRows } from './fee-rows';
import GrandTotal from './grand-total';
import { SubTotal } from './subtotal';
import TaxesAndFees from './taxes-and-fees';
import { TaxesRow } from './taxes-row';

const Wrapper = ({ style, ...props }: BoxProps) => (
  <Box {...props} style={[styles.wrapper, style]} />
);

const Separator = () => <Box style={styles.separator} />;

const DeliveryLogoContainer = ({ style, ...props }: BoxProps) => (
  <Box {...props} style={[styles.deliveryLogoContainer, style]} />
);

interface TotalProps {
  isLoading?: boolean;
}

export const Totals: FC<TotalProps> = () => {
  const { calculateCartTotalWithDiscount } = useCart();
  const {
    totals,
    serverOrder,
    cart,
    serviceMode,
    isDeliveryOrder,
    cartEntries,
  } = useCheckoutContext();
  const { isOrderPriceLoading } = useCheckoutLoadingState();

  const selectedDonationCartEntry = useMemo(
    () => cartEntries.find((cartEntry: ILegacyCartEntry) => cartEntry.isDonation),
    [cartEntries]
  );

  const donationAmount = useMemo(() => selectedDonationCartEntry?.price ?? 0, [
    selectedDonationCartEntry,
  ]);
  const showItemDonationRow = donationAmount > 0;

  const {
    subTotalCents = 0,
    taxCents = 0,
    rewardDiscountAmount,
    tipAmount,
    additionalDiscountAmount = 0,
    deliveryFee = null,
    deliveryFeeDiscount = null,
  } = totals || {};

  const { delivery } = serverOrder ?? {};

  const { featureDisclaimers } = useFeatureDisclaimers();
  const { deliveryLogo } = featureDisclaimers || {};
  const { cartTotal = 0, isCartTotalNegative = false } = calculateCartTotalWithDiscount();
  const isSignUpAfterCart = useIsSignUpAfterCart();

  // If "SignUp After Cart" is enabled:
  // - Use cart preview values
  // Otherwise
  // - Use server cart
  const skipOrderPricing = isSignUpAfterCart || isCartTotalNegative;
  const cartSelected = skipOrderPricing
    ? ({
        totalCents: cartTotal,
        subTotalCents: cartTotal,
      } as IBackendCart)
    : cart;

  const deliveryLoading = serverOrder?.delivery?.status === DeliveryStatus.QUOTE_REQUESTED;
  const dropoffState = serverOrder?.delivery?.dropoff?.state;
  const otherDiscountAmount = isCartTotalNegative ? 0 : additionalDiscountAmount;
  const rewardDiscount = isCartTotalNegative ? 0 : rewardDiscountAmount;

  return (
    <Wrapper>
      <Box>
        {showItemDonationRow ? (
          <Box style={styles.donationRow}>
            <DonationsRow
              isLoading={isOrderPriceLoading && !skipOrderPricing}
              totalCents={donationAmount}
              message={t('donation')}
              textProps={{ fontSize: 'md' }}
            />
          </Box>
        ) : null}

        <SubTotal
          cart={cartSelected}
          isLoading={isOrderPriceLoading && !skipOrderPricing}
          otherDiscountAmount={otherDiscountAmount}
          rewardDiscount={rewardDiscount}
        />

        <FeeRows
          fees={cartSelected?.fees}
          isLoading={isOrderPriceLoading && !skipOrderPricing}
          textProps={{ fontSize: 'md' }}
        />

        {isDeliveryOrder ? (
          <TaxesAndFees
            deliveryFee={deliveryFee}
            deliveryFeeDiscount={deliveryFeeDiscount}
            dropoffState={dropoffState}
            serviceFeeCents={delivery?.serviceFeeCents ?? 0}
            smallCartFeeCents={delivery?.smallCartFeeCents ?? 0}
            geographicalFeeCents={delivery?.geographicalFeeCents ?? 0}
            taxCents={taxCents}
            serviceMode={serviceMode}
            subTotalCents={subTotalCents}
            isLoading={isOrderPriceLoading || deliveryLoading}
          />
        ) : (
          <TaxesRow
            mt={'$2'}
            textProps={{ fontSize: 'md' }}
            taxCents={taxCents}
            isLoading={isOrderPriceLoading}
          />
        )}

        {isDeliveryOrder && (
          <Box style={styles.tipRow}>
            <Typography fontSize="md">{t('tip')}</Typography>
            <Currency textProps={{ fontSize: 'md' }} amount={tipAmount} />
          </Box>
        )}
        <Separator />

        <GrandTotal isLoading={isOrderPriceLoading && !skipOrderPricing} />
      </Box>
      {isDeliveryOrder && deliveryLogo?.locale ? (
        <DeliveryLogoContainer>
          <Picture image={deliveryLogo?.locale} alt={t('deliveryLogoAlt')} />
        </DeliveryLogoContainer>
      ) : null}
    </Wrapper>
  );
};

export default React.memo(Totals);

const styles = StyleSheet.create({
  deliveryLogoContainer: {
    alignSelf: 'center',
    width: spacing.$40,
  },
  donationRow: {
    marginBottom: spacing.$3,
  },
  separator: {
    backgroundColor: primitive.$greyFour,
    height: 1,
    marginTop: spacing.$4,
  },
  tipRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.$1,
  },
  wrapper: {
    marginVertical: spacing.$8,
    width: layout.full,
  },
});
